// Mapeo de Intelligence + economía de tokens (0.6.4, doc 17).
// Funciones PURAS (testeables sin React): parseo defensivo del jsonb que
// devuelve el RPC dl_intelligence_status y las reglas de "¿puedo pagar esta
// búsqueda?". La verdad vive en el SERVIDOR (ledger + RPCs dl_*): el RPC ya
// emite también free_searches_per_day y daily_earn_cap (fix de la migración);
// estas constantes son solo el fallback de pintado mientras el status no ha
// llegado (o con un backend sin ese fix) — mantener en sincronía.

export const DL_INTEL_FREE_SEARCHES_PER_DAY = 1; // 1 búsqueda gratis/día UTC (antes 5)
export const DL_INTEL_SEARCH_COST = 1;           // búsqueda jugador/alianza, en tokens
export const DL_INTEL_REGION_SEARCH_COST = 3;    // volcado de región completa, en tokens
// Tope de ganancia: 0 = SIN tope (migración 011, decisión del dueño 2026-08-02
// — mientras más mapea, más gana). El valor solo llega del backend; este
// fallback cubre backends anteriores a la 011 (que aún recortan a 200).
export const DL_INTEL_DAILY_EARN_CAP = 200;

// Progreso del mapeo de UNA región (cobertura que pinta la web).
export interface DlIntelligenceRegionProgress {
  regionId: string;
  cellsTotal: number;
  cellsScanned: number;
  // Celdas completadas HOY (UTC) por los bots de este usuario.
  scannedToday: number;
}

// Respuesta de dl_intelligence_status en una llamada: saldo + cuota gratis +
// costes + (si se pidió región) su cobertura.
export interface DlIntelligenceStatus {
  balance: number;
  freeSearchesPerDay: number;
  freeSearchesLeft: number;
  searchCost: number;
  regionSearchCost: number;
  earnedToday: number;
  dailyEarnCap: number;
  region: DlIntelligenceRegionProgress | null;
}

function asInt(value: unknown, fallback: number): number {
  const n = typeof value === 'string' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) ? Math.trunc(n) : fallback;
}

// jsonb del RPC → dominio. Defensivo a propósito: si el backend aún no envía
// un campo (despliegue por fases), la UI cae a las constantes de arriba.
export function mapDlIntelligenceStatus(raw: unknown): DlIntelligenceStatus {
  const row = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const regionRow = (row.region && typeof row.region === 'object' ? row.region : null) as
    | Record<string, unknown>
    | null;
  // Nombres de campo = los que emite dl_intelligence_status (migración 009):
  // region: {region_id, total_cells, scanned_cells, my_cells_today}.
  const region: DlIntelligenceRegionProgress | null =
    regionRow && regionRow.region_id
      ? {
          regionId: String(regionRow.region_id),
          cellsTotal: asInt(regionRow.total_cells, 0),
          cellsScanned: asInt(regionRow.scanned_cells, 0),
          scannedToday: asInt(regionRow.my_cells_today, 0)
        }
      : null;
  return {
    balance: asInt(row.balance, 0),
    freeSearchesPerDay: asInt(row.free_searches_per_day, DL_INTEL_FREE_SEARCHES_PER_DAY),
    freeSearchesLeft: asInt(row.free_searches_left, 0),
    searchCost: asInt(row.search_cost, DL_INTEL_SEARCH_COST),
    regionSearchCost: asInt(row.region_search_cost, DL_INTEL_REGION_SEARCH_COST),
    earnedToday: asInt(row.earned_today, 0),
    dailyEarnCap: asInt(row.daily_earn_cap, DL_INTEL_DAILY_EARN_CAP),
    region
  };
}

// Coste de la búsqueda que el usuario está a punto de lanzar.
export function dlSearchCost(status: DlIntelligenceStatus, isRegionSearch: boolean): number {
  return isRegionSearch ? status.regionSearchCost : status.searchCost;
}

// ¿Puede pagarla? Gratis del día primero; después, saldo de tokens.
export function canAffordSearch(status: DlIntelligenceStatus, isRegionSearch: boolean): boolean {
  if (status.freeSearchesLeft > 0) return true;
  return status.balance >= dlSearchCost(status, isRegionSearch);
}

// Cobertura en % entero (para la barra de progreso); acotado a 0..100.
export function coveragePercent(scanned: number, total: number): number {
  if (!Number.isFinite(scanned) || !Number.isFinite(total) || total <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((scanned / total) * 100)));
}

// Región del mapeo: 3-5 dígitos (mismo criterio que validará el backend en
// ensure_region_seeded). '' o null = sin configurar.
export function isValidMappingRegion(value: string | null | undefined): boolean {
  return /^\d{3,5}$/.test((value ?? '').trim());
}

// Resultado de ensure_region_seeded (migración 009): la rama "ya sembrada"
// devuelve {seeded:false, region_id, total_cells}; la rama "recién clonada"
// devuelve {seeded:true, region_id, inserted} (inserted ≈ celdas de la región).
export interface RegionSeedResult {
  regionId: string;
  // true = la región se acaba de clonar; false = ya estaba sembrada en la red.
  seeded: boolean;
  totalCells: number;
}

export function mapRegionSeedResult(raw: unknown): RegionSeedResult {
  const row = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const seeded = row.seeded === true || asInt(row.inserted, 0) > 0;
  return {
    regionId: String(row.region_id ?? ''),
    seeded,
    totalCells: asInt(row.total_cells ?? row.inserted, 0)
  };
}

// --- Shield Monitor (0.6.20) -------------------------------------------------
// HISTORIAL de la vigilancia: el panel mostraba solo el instante actual, así
// que no se podía saber cuándo cayó el escudo, cuándo volvió, ni si la
// vigilancia seguía viva. Se guardan los CAMBIOS de estado (no cada
// comprobación: con un chequeo por minuto sería ruido).
export type ShieldWatchState =
  | 'shielded' | 'confirming' | 'down' | 'moved' | 'unknown'
  // Auto Shield (0.6.28): eventos sobre TU ciudad — misma tabla y misma
  // tubería de correo/captura que la vigilancia, familia distinta.
  | 'protected' | 'attack_halted';

// Familia del evento: 'city' = ataques a tu ciudad (Auto Shield), 'watch' =
// vigilancia de un objetivo (Shield Monitor). El historial del panel de
// vigilancia solo muestra 'watch'; el feed de Actividad de la flota lo muestra
// todo.
export function shieldEventFamily(state: ShieldWatchState): 'city' | 'watch' {
  return state === 'protected' || state === 'attack_halted' ? 'city' : 'watch';
}

export interface ShieldWatchEvent {
  state: ShieldWatchState;
  x: number | null;
  y: number | null;
  regionId: string | null;
  targetName: string | null;
  // Probabilidad de cúpula que midió el detector (0..1); útil para auditar.
  domeP: number | null;
  at: string;                 // ISO del momento del cambio
  notified: boolean;          // si generó correo (solo sospecha y confirmación)
  // PRUEBA VISUAL: el frame con el que el bot decidió. El historial solo dice
  // si la hay — la imagen pesa y se pide aparte al pulsar sobre el evento.
  // Solo las 5 últimas por bot la conservan; las viejas quedan sin imagen.
  hasCapture: boolean;
  instanceId: string;
  // Sello del evento (el mismo que usa la BD): junto a instancia y estado
  // identifica la fila cuya captura hay que pedir.
  stamp: number;
}

export function mapShieldWatchEvents(raw: unknown): ShieldWatchEvent[] {
  const rows = Array.isArray(raw) ? raw : [];
  return rows.map((item) => {
    const row = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
    const state = String(row.state ?? 'unknown') as ShieldWatchState;
    const p = row.dome_p === null || row.dome_p === undefined ? null : Number(row.dome_p);
    return {
      state,
      x: row.target_x === null || row.target_x === undefined ? null : asInt(row.target_x, 0),
      y: row.target_y === null || row.target_y === undefined ? null : asInt(row.target_y, 0),
      regionId: row.region_id ? String(row.region_id) : null,
      targetName: row.target_name ? String(row.target_name) : null,
      domeP: p !== null && Number.isFinite(p) ? p : null,
      at: String(row.created_at ?? ''),
      notified: !!row.notified_at,
      hasCapture: !!row.has_capture,
      instanceId: String(row.instance_id ?? ''),
      stamp: asInt(row.at, 0)
    };
  });
}
